// Who operates ConfirmPH, in the site footer.
//
// Reads the canonical operator record (/api/legal/operator) instead of
// hardcoding a corporate identity anywhere in the frontend. Until a real
// registered entity is recorded and verified, that record says so, and this
// component renders the honest statement rather than a placeholder that
// hints at a registration nobody has started.
//
// Fails quiet: if the endpoint cannot be reached the footer simply omits
// the operator line rather than falling back to an invented one.
import { useEffect, useState } from "react";

export default function OperatorIdentity({ testId = "footer-operator" }) {
  const [operator, setOperator] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Imported lazily so the public footer never pulls the authenticated
    // API client into the critical path for a visitor who is signed out.
    import("../lib/api")
      .then(({ api }) => api.get("/legal/operator"))
      .then((r) => { if (!cancelled) setOperator(r.data); })
      .catch(() => { /* footer stays silent rather than guessing */ });
    return () => { cancelled = true; };
  }, []);

  if (!operator) return null;

  return (
    <div className="space-y-1" data-testid={testId}>
      <div data-testid="footer-operator-name">
        Operated by {operator.operator_name}
        {operator.entity_type ? ` (${operator.entity_type})` : ""}
      </div>
      {operator.registered_office && (
        <div data-testid="footer-operator-address">{operator.registered_office}</div>
      )}
      {operator.registrations?.length > 0 && (
        <div data-testid="footer-operator-registrations">
          Registered with {operator.registrations.map((r) => r.authority).join(", ")}
        </div>
      )}
      {operator.dpo?.name && (
        <div data-testid="footer-operator-dpo">
          Data Protection Officer: {operator.dpo.name}
          {operator.dpo.contact ? ` · ${operator.dpo.contact}` : ""}
        </div>
      )}
      {operator.trustmark && (
        <div data-testid="footer-operator-trustmark">
          Philippine Trustmark holder
          {operator.trustmark.expires_at ? ` (valid to ${operator.trustmark.expires_at})` : ""}
        </div>
      )}
      {!operator.legal_entity_recorded && (
        <div data-testid="footer-operator-disclosure" className="max-w-2xl mx-auto">
          {operator.disclosure}
        </div>
      )}
    </div>
  );
}
